import type { Texture } from 'three';

/**
 * 차량 표면에 붙이는 데칼 이미지 목록.
 *
 * 왜 모델별이 아니라 전역 목록인가:
 *   훅은 조건부로 호출할 수 없습니다. 모델마다 데칼 개수가 다른데 그때마다
 *   useTexture에 넘기는 배열 길이가 바뀌면 모델을 바꿀 때 훅 순서가 흔들립니다.
 *   그래서 앱이 쓰는 데칼 전체를 여기 고정해 두고 항상 같은 배열로 불러온 뒤,
 *   모델은 id로 골라 쓰기만 합니다.
 *
 * 포르쉐 크레스트 출처:
 *   2017 Porsche 718 Boxster S 에셋(Ddiaz Design, CC-BY-4.0)의 배지 아틀라스
 *   `assets/models/2017_porsche_718_boxster_s/textures/badges_baseColor.png`에서
 *   잘라냈습니다.
 *   추출 명령: scripts/extract-decal.mjs ... --rect=318,50,186,262 --flip-y
 *
 *   왜 --flip-y인가:
 *     아틀라스에는 크레스트가 상하 반전된 상태로 저장돼 있습니다. 반전을 되돌리지
 *     않으면 판에 붙었을 때 크레스트가 180도 돌아가 꼭지점이 위로, PORSCHE 글자가
 *     아래에 거울 반전으로 나옵니다.
 *     방향은 Boxster 실내 렌더와 비교해 확정했습니다. 그 에셋은 원저자의 UV로
 *     같은 아틀라스를 읽으므로 올바른 방향의 기준이 됩니다.
 *     기준: PORSCHE 글자가 위, 뿔이 좌상단·우하단, 적/흑 줄무늬가 우상단·좌하단,
 *           말은 왼쪽을 보고 방패 꼭지점은 아래.
 *
 *   Taycan 에셋에는 크레스트가 색이나 텍스처로 존재하지 않습니다(부조 형상만 있음).
 *   같은 프로젝트 안의 다른 CC-BY 에셋에서 가져와 씁니다. 두 에셋 모두 저작자
 *   표시 의무가 있으므로 크레딧에서 빠지면 안 됩니다.
 *
 *   ⚠ 포르쉐 크레스트는 등록상표입니다. CC-BY는 모델러의 저작권만 다루므로
 *     상표권은 별개입니다. 상업적 공개 시에는 이 데칼을 빼는 것을 검토하세요.
 */
/**
 * 테슬라 워드마크 출처:
 *   2024 Tesla Model 3 + Interior 에셋(tonielpro520, CC-BY-4.0)의 배지 아틀라스
 *   `assets/models/2024_tesla_model_3__interior/textures/EXT_Badge_AT_baseColor.png`에서
 *   잘라냈습니다.
 *   추출 명령: scripts/extract-decal.mjs ... --rect=130,144,348,29 --flip-y --alpha-from-luminance
 *
 *   이 아틀라스는 알파가 없는 RGB 3채널이고 검은 배경에 흰 무늬입니다. 그래서
 *   밝기를 알파로 바꿔야 합니다(--alpha-from-luminance). 그냥 쓰면 검은 사각형이
 *   함께 붙습니다. 상하 반전도 포르쉐 아틀라스와 같은 이유로 필요합니다.
 *
 *   왜 이 데칼이 필요한가: 이 에셋의 스티어링 허브에는 TESLA 워드마크가 양각
 *   형상으로 새겨져 있는데, 50 x 6 mm 크기에 부조가 ±1mm뿐이고 머티리얼이
 *   무광(roughness 0.94)이라 빛을 거의 못 받습니다. 그래서 글자가 뭉개져
 *   보입니다. 형상 자체는 온전합니다(원본을 무손실 변환해 렌더해도 동일).
 *
 *   ⚠ 테슬라 워드마크도 등록상표입니다. 포르쉐 크레스트와 같은 주의가 필요합니다.
 */
export const DECAL_IMAGES = {
  porscheCrest: '/brand/porsche-crest.png',
  teslaWordmark: '/brand/tesla-wordmark.png',
} as const;

/**
 * 데칼 이미지 식별자. 카탈로그(data/carModels.ts)의 `decal.image`가 이 목록을
 * 벗어나면 컴파일 시점에 걸립니다. 예전에는 오타가 런타임에 "데칼이 안 보임"으로만
 * 드러났습니다.
 */
export type DecalImageId = keyof typeof DECAL_IMAGES;

export const DECAL_IDS = Object.keys(DECAL_IMAGES) as DecalImageId[];
export const DECAL_IMAGE_URLS: string[] = DECAL_IDS.map((id) => DECAL_IMAGES[id]);

/** 데칼 id -> 텍스처. 아직 안 불러온 항목은 null입니다. */
export type DecalTextureMap = Record<DecalImageId, Texture | null>;

/**
 * useTexture가 돌려준 배열을 id로 찾을 수 있게 묶습니다.
 * @param textures DECAL_IMAGE_URLS와 같은 순서
 */
export function mapDecalTextures(textures: Texture[] | Texture): DecalTextureMap {
  const list = Array.isArray(textures) ? textures : [textures];
  const byId = {} as DecalTextureMap;
  DECAL_IDS.forEach((id, index) => {
    byId[id] = list[index] ?? null;
  });
  return byId;
}
